
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../components/Toast';
import { api, ApiError } from '../../lib/api';
import { keys, useTree } from '../../lib/queries';
import { formatDateTime, useLang, useT } from '../../i18n';
import { BackLink } from '../../components/BackLink';

interface Entry { id: string; action: string; entityId: string; userId: string | null; userName: string | null; createdAt: number; revertedBy: string | null }

export function History({ treeId }: { treeId: string }) {
  const t = useT();
  const lang = useLang();
  const tree = useTree(treeId);
  const qc = useQueryClient();
  const toast = useToast((s) => s.show);
  const list = useQuery({ queryKey: keys.history(treeId), queryFn: () => api<{ entries: Entry[] }>('GET', `/trees/${treeId}/history`).then((r) => r.entries) });
  const undo = useMutation({
    mutationFn: (id: string) => api('POST', `/trees/${treeId}/history/${id}/revert`),
    onSuccess: () => {
      toast(t.history.undone);
      void qc.invalidateQueries({ queryKey: keys.history(treeId) });
      void qc.invalidateQueries({ queryKey: keys.tree(treeId) });
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : t.history.couldntUndo),
  });
  const names = new Map(tree.data?.people.map((p) => [p.id, [p.firstName, p.lastName].filter(Boolean).join(' ')]));
  return (
    <div className="page">
      <div className="row" style={{ marginBottom: 16 }}>
        <BackLink fallback={{ to: '/t/$treeId', params: { treeId } }} aria-label={t.common.backToTree}>←</BackLink>
        <h1 className="grow">{t.history.title}</h1>
      </div>
      <div className="stack">
        {list.isLoading && <p className="muted">{t.common.loading}</p>}
        {list.error && <p className="error">{(list.error as Error).message}</p>}
        {list.data?.length === 0 && <p className="muted">{t.history.empty}</p>}
        {list.data?.map((e) => (
          <div key={e.id} className="list-item" style={{ cursor: 'default', opacity: e.revertedBy ? 0.55 : 1 }}>
            <div className="grow">
              <strong>{t.history.actions[e.action] ?? e.action}</strong>
              {names.get(e.entityId) ? ` · ${names.get(e.entityId)}` : ''}
              <div className="muted" style={{ fontSize: 13 }}>{e.userName ?? t.common.someone} · {formatDateTime(e.createdAt, lang)}</div>
            </div>
            {e.revertedBy ? <span className="pill">{t.history.undone}</span> : e.action !== 'revert' && (
              <button className="btn btn-sm" disabled={undo.isPending} onClick={() => undo.mutate(e.id)}>{t.history.undo}</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
